/**
 * Render the prescription a coach set for one exercise: sets, reps or hold, and rest.
 *
 * These live on `moves[].videos[]` in a block — `sets/setsMin/setsMax`,
 * `reps/repsMin/repsMax`, `repType` ('reps' | 'seconds') and
 * `restSec/restSecMin/restSecMax`. 808 blocks carry them, but the only thing shown when
 * reviewing a client's result was the free-text note (`text`/`text_ar`), so the coach
 * could see what the client ACHIEVED without seeing what they had ASKED for — which is
 * the comparison the whole result view exists to make.
 *
 * Min and max are separate because ranges are used heavily (833 of ~2,000 videos
 * sampled): "3-4 sets x 10-12 reps" has to survive as a range, not collapse to one number.
 */

const range = (min, max, fallback) => {
  const lo = min ?? fallback;
  const hi = max ?? fallback;
  if (lo == null && hi == null) return null;
  if (lo == null) return String(hi);
  if (hi == null) return String(lo);
  return Number(lo) === Number(hi) ? String(lo) : `${lo}–${hi}`; // en dash
};

/**
 * @param {object} v a video entry from block.moves[].videos[]
 * @returns {string|null} e.g. "4 sets x 10-12 reps - rest 90s", or null when nothing was set
 */
export const formatPrescription = (v) => {
  if (!v) return null;

  const sets = range(v.setsMin, v.setsMax, v.sets);
  const reps = range(v.repsMin, v.repsMax, v.reps);
  const rest = range(v.restSecMin, v.restSecMax, v.restSec);

  const parts = [];
  if (sets) parts.push(`${sets} ${Number(sets) === 1 ? 'set' : 'sets'}`);
  if (reps) {
    // repType 'seconds' means a timed hold, which is why a result reads "10s hold"
    // rather than a rep count.
    parts.push(v.repType === 'seconds' ? `${reps}s hold` : `${reps} reps`);
  }

  const main = parts.join(' × '); // multiplication sign
  const restText = rest ? `rest ${rest}s` : '';

  if (main && restText) return `${main} · ${restText}`;
  return main || restText || null;
};

export default formatPrescription;
